// src/transformations/parcelDataTransformations.js

/**
 * BACKEND FIELD NAMING CONVENTION:
 *
 * The backend returns camelCase fields with 'parcel' prefix:
 * - parcelId, parcelStatus, parcelPrice
 * - parcelPickupAddress, parcelDeliveryAddress
 * - parcelCreatedAt, parcelUpdatedAt
 * - parcelWeightKg, parcelVolumeCbm
 * - parcelParcelType (array of types)
 * - parcelRiderName, parcelRiderPhone, parcelRiderId
 * - parcelIsMultiStage, parcelTotalLegs, parcelCurrentLegId
 *
 * USAGE: Components should use backend field names directly.
 * Only use this transform when you need legacy 'from/to/date' format.
 */

// Transform API (camelCase with parcel* prefix) booking to UI display format
// NOTE: Prefer using backend fields directly in new components
function transformBooking(apiBooking) {
  if (!apiBooking) return {};

  return {
    id: apiBooking.parcelId,
    parcelId: apiBooking.parcelId,
    from: apiBooking.parcelPickupAddress || {},
    to: apiBooking.parcelDeliveryAddress || {},
    parcelType: apiBooking.parcelParcelType || [],
    itemType: Array.isArray(apiBooking.parcelParcelType)
      ? apiBooking.parcelParcelType.join(', ')
      : apiBooking.parcelParcelType || 'Parcel',
    weight: apiBooking.parcelWeightKg || apiBooking.parcelWeight || '',
    size:
      apiBooking.parcelWeightKg || apiBooking.parcelWeight || apiBooking.parcelVolumeCbm || 'N/A',
    price: apiBooking.parcelPrice || '',
    status: apiBooking.parcelStatus || '',
    statusColor: getStatusColor(apiBooking.parcelStatus),
    trackingNumber: apiBooking.parcelTrackingNumber || '',
    estimatedDeliveryDate: apiBooking.parcelEstimatedDeliveryDate || '',
    deliveredAt: apiBooking.parcelDeliveredAt || '',
    createdAt: apiBooking.parcelCreatedAt || '',
    updatedAt: apiBooking.parcelUpdatedAt || '',
    dateTime: formatDateTime(apiBooking.parcelCreatedAt || apiBooking.parcelUpdatedAt),
    userId: apiBooking.parcelUserId,
    volume: apiBooking.parcelVolumeCbm || apiBooking.parcelVolume || '',
    deliveryEstimate: apiBooking.parcelDeliveryEstimate || '',
    description: apiBooking.parcelDescription || '',
    deliveryInstructions: apiBooking.parcelDeliveryInstructions || '',
    receiverName: apiBooking.parcelDeliveryAddress?.name || '',
    receiverPhone: apiBooking.parcelDeliveryAddress?.phone || '',
    riderName: apiBooking.parcelRiderName || null,
    riderPhone: apiBooking.parcelRiderPhone || null,
    riderVehicle: apiBooking.parcelRiderVehicle || null,
    // Multi-stage fields
    isMultiStage: apiBooking.parcelIsMultiStage || false,
    handoffCount: apiBooking.parcelHandoffCount || 0,
    currentLegSequence: apiBooking.parcelCurrentLegId || 1,
    totalLegs: apiBooking.parcelTotalLegs || 1,
  };
}

// Helper to get status color
function getStatusColor(status) {
  const statusLower = (status || '').toLowerCase();
  if (statusLower.includes('delivered')) return '#4caf50';
  if (statusLower.includes('transit') || statusLower.includes('picked')) return '#2196f3';
  if (statusLower.includes('cancelled')) return '#f44336';
  if (statusLower.includes('pending')) return '#ff9800';
  if (statusLower.includes('out for delivery')) return '#9c27b0';
  return '#667eea';
}

// Helper to format date/time
function formatDateTime(dateString) {
  if (!dateString) return '';
  try {
    const date = new Date(dateString);
    return date.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return dateString;
  }
}

module.exports = { transformBooking };
